/**
 * Time to first visible ad: how long from the start of a page load until the
 * first ad unit has content and is at least half on screen. Sent to Datadog RUM
 * as the `first_ad_visible` duration vital, once per page load.
 *
 * Display ads report through the Google Publisher Tag events in
 * plugins/first-ad-visible.client.ts. Outbrain, Taboola and Primis call
 * `watchAdVisibility` with their container once they have rendered.
 */

import type { AdVisibleTiming } from './firstAdStages'

export const FIRST_AD_VISIBLE_VITAL = 'first_ad_visible'

/** Past this, the first ad no longer says anything about how the page loaded. */
export const FIRST_AD_VISIBLE_MAX_MS = 60_000

/** The share of an ad that has to be on screen, the 50% the viewability rules use. */
export const AD_VISIBLE_RATIO = 0.5

export type AdType = 'display' | 'outbrain' | 'taboola' | 'primis'

export interface FirstAdVisibleSample {
  /** Epoch ms the page load started for the visitor. */
  startTime: number
  /** Ms from then until the first ad was visible. */
  duration: number
  adType: AdType
  placement: string
  /** When the ad was requested, rendered and came into view, see firstAdStages.ts. */
  timing?: AdVisibleTiming
}

export interface FirstAdVisibleTracker {
  /** An ad is on screen. Only the first one, within the cap, is reported. */
  adVisible: (
    adType: AdType,
    placement: string,
    timing?: AdVisibleTiming
  ) => void
  /** The page load stopped being one the timing can describe. */
  cancel: () => void
  readonly settled: boolean
}

/** Thresholds every ad gets, as shares of its own height. */
const BASE_THRESHOLDS = [0, 0.1, 0.25, AD_VISIBLE_RATIO, 0.75, 1]

export function createFirstAdVisibleTracker({
  startTime,
  elapsed,
  report,
  onSettle,
}: {
  /** Epoch ms the page load started, read when the ad turns visible. */
  startTime: () => number
  /** Ms since `startTime`, read when the ad turns visible. */
  elapsed: () => number
  report: (sample: FirstAdVisibleSample) => void
  /** Runs once, when the page load is reported or cancelled. */
  onSettle?: () => void
}): FirstAdVisibleTracker {
  let settled = false
  const settle = () => {
    settled = true
    onSettle?.()
  }
  return {
    adVisible(adType, placement, timing) {
      if (settled) return
      const duration = elapsed()
      if (duration >= 0 && duration <= FIRST_AD_VISIBLE_MAX_MS) {
        report({ startTime: startTime(), duration, adType, placement, timing })
      }
      settle()
    },
    cancel() {
      if (!settled) settle()
    },
    get settled() {
      return settled
    },
  }
}

let activeTracker: FirstAdVisibleTracker | undefined

/** Set by the plugin for the page load. Nothing is reported until it runs. */
export function setFirstAdVisibleTracker(
  tracker: FirstAdVisibleTracker | undefined
) {
  activeTracker = tracker
}

export function reportAdVisible(
  adType: AdType,
  placement: string,
  timing?: AdVisibleTiming
) {
  activeTracker?.adVisible(adType, placement, timing)
}

const watchers = new Set<() => void>()

/** Stops every container still being watched, once the page load is settled. */
export function stopWatchingAds() {
  watchers.forEach((stop) => stop())
}

/**
 * The thresholds to watch an ad with. An ad taller than the screen also gets
 * the share of its height that fills half the screen, since the observer only
 * reports when one of its thresholds is crossed. Rounded up to a whole percent
 * so a growing feed does not need a new observer for every pixel.
 */
export function visibilityThresholds(
  height: number,
  viewportHeight: number
): number[] {
  const fillsHalfTheScreen =
    Math.ceil(((viewportHeight * AD_VISIBLE_RATIO) / height) * 100) / 100
  if (
    !(fillsHalfTheScreen > 0 && fillsHalfTheScreen < AD_VISIBLE_RATIO) ||
    BASE_THRESHOLDS.includes(fillsHalfTheScreen)
  ) {
    return BASE_THRESHOLDS
  }
  return [...BASE_THRESHOLDS, fillsHalfTheScreen].sort((a, b) => a - b)
}

/**
 * Whether an observed ad counts as on screen. Half of it in view, or, for an ad
 * taller than half the screen, enough of it to fill half the screen, since a
 * long native block can never be half visible. A container with nothing laid
 * out in it yet has no height and does not count.
 */
export function isAdEntryVisible(entry: IntersectionObserverEntry): boolean {
  if (!entry.isIntersecting || entry.boundingClientRect.height <= 0) {
    return false
  }
  if (entry.intersectionRatio >= AD_VISIBLE_RATIO) return true
  const viewportHeight = entry.rootBounds?.height ?? 0
  return (
    viewportHeight > 0 &&
    entry.intersectionRect.height >= viewportHeight * AD_VISIBLE_RATIO
  )
}

/**
 * Reports the ad as visible the first time its container counts as on screen.
 * Call it once the ad has rendered into `element`. Returns a function that
 * stops watching, for the component's unmount.
 */
export function watchAdVisibility(
  element: Element | null | undefined,
  adType: AdType,
  placement: string
): () => void {
  if (
    !element ||
    !activeTracker ||
    activeTracker.settled ||
    typeof IntersectionObserver === 'undefined'
  ) {
    return () => {}
  }

  try {
    return observeUntilVisible(element, adType, placement)
  } catch {
    // A measurement only. An observer that fails to start leaves the ad alone.
    return () => {}
  }
}

function observeUntilVisible(
  element: Element,
  adType: AdType,
  placement: string
): () => void {
  const renderedAt = performance.now()
  let stopObserving = () => {}
  const stop = () => {
    stopObserving()
    watchers.delete(stop)
  }
  watchers.add(stop)
  stopObserving = observeUntilAdVisible(element, () => {
    stop()
    reportAdVisible(adType, placement, {
      renderedAt,
      inViewAt: performance.now(),
    })
  })
  return stop
}

/**
 * Calls `onVisible` the first time `element` counts as on screen (see
 * `isAdEntryVisible`), then stops watching. Follows the container's size: an
 * empty container that gains height, or an ad that grows past the screen, gets
 * a new observer with the right thresholds. Returns a function that stops
 * watching. Throws if the observer cannot start.
 */
export function observeUntilAdVisible(
  element: Element,
  onVisible: () => void
): () => void {
  let intersectionObserver: IntersectionObserver | undefined
  let resizeObserver: ResizeObserver | undefined
  let done = false
  const stop = () => {
    done = true
    intersectionObserver?.disconnect()
    resizeObserver?.disconnect()
  }
  let thresholds: number[] | undefined
  const observe = (height: number) => {
    thresholds = visibilityThresholds(height, window.innerHeight)
    intersectionObserver?.disconnect()
    intersectionObserver = new IntersectionObserver(
      (entries) => {
        if (done || !entries.some(isAdEntryVisible)) return
        stop()
        onVisible()
      },
      { threshold: thresholds }
    )
    intersectionObserver.observe(element)
  }

  const initialHeight = element.getBoundingClientRect().height
  observe(initialHeight)
  // An empty container already counts as fully in view, so the ad filling it
  // crosses no threshold, and a growing ad needs its half-screen threshold
  // moved. A new observer covers both, and gets a fresh reading.
  if (typeof ResizeObserver !== 'undefined') {
    let hadHeight = initialHeight > 0
    resizeObserver = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height ?? 0
      if (done || height <= 0) return
      const next = visibilityThresholds(height, window.innerHeight)
      if (!hadHeight || next.join() !== thresholds?.join()) observe(height)
      hadHeight = true
    })
    resizeObserver.observe(element)
  }
  return stop
}
